// types.ts - the grade-reveal vocabulary and contracts (S22).
//
// The UI grade language is Chess.com's move-class palette (the CSS token layer in tokens.css). It is a
// DIFFERENT axis from the engine's `GradeBand` (excellent/good/acceptable/marginal/bad/punt, in
// @postflop/schema): the engine scores EV-loss into a band; the UI paints a move class. `MOVE_CLASS_FROM_BAND`
// is the honest seam between them - S24's GENERATED Solo hands carry a real engine `GradeBand` + a real
// `CoachText`, and render through the SAME <GradeReveal> by mapping band -> move class here.
//
// The dx5 Diagnose teaching hand is AUTHORED content (PRODUCT_NORTH_STAR §5, same sanction as the felt
// cards and the Observe narration): its move classes and coaching prose are transcribed 1:1 from the
// locked mockup in ./data.ts, not produced by the engine. So S22 invokes NO /api/coach prose lane -> $0,
// no COST GATE, and the digest firewall holds by construction (no prose lane runs).
import type { GradeBand } from '@postflop/schema';

/** The Chess.com move classes = the grade color language (tokens.css --best/--great/--exc/...). `book` is
 *  the preflop-chart "book move" (unused by this teaching hand). */
export type MoveClass = 'best' | 'great' | 'exc' | 'inacc' | 'mist' | 'blun' | 'book';

/** clsName in the mockup: the human label for a move class (colored text before the action). */
export const MOVE_CLASS_LABELS: Record<MoveClass, string> = {
  best: 'Best',
  great: 'Great',
  exc: 'Excellent',
  inacc: 'Inaccuracy',
  mist: 'Mistake',
  blun: 'Blunder',
  book: 'Book',
};

/** The honest engine -> UI seam. A generated hand's `GradeBand` (engine/grading) maps to a move class so
 *  the same <GradeReveal> renders authored (dx5) and generated (S24) grades identically. Monotonic:
 *  best play -> best color. */
export const MOVE_CLASS_FROM_BAND: Record<GradeBand, MoveClass> = {
  excellent: 'best',
  good: 'great',
  acceptable: 'exc',
  marginal: 'inacc',
  bad: 'mist',
  punt: 'blun',
};

/** One graded axis (A = the hand/play, B = the read/game). Colored heading + coaching paragraph. */
export interface AxisGrade {
  /** 'A' (the play) or 'B' (the read). */
  badge: 'A' | 'B';
  /** the small uppercase eyebrow ("Your play, the hand" / "Your read, the game"). */
  kicker: string;
  /** the move class driving color for the badge + heading. */
  cls: MoveClass;
  /** the action/read label after the class name ("Raise to isolate", "Read the room right"). */
  label: string;
  /** the coaching paragraph. */
  text: string;
}

/** The verdict headline card at the top of the reveal (well-read-well-played, etc.). */
export interface Verdict {
  /** move class driving the mark badge color. */
  cls: MoveClass;
  /** the mark glyph: check when both right, "!" when one axis off, cross when both wrong. */
  mark: string;
  /** the headline ("Well read, well played"). */
  label: string;
  /** the sub line under the headline. */
  sub: string;
}

/** The coach callout box below the two axes. `bad` red-borders it and flips the lead color (the
 *  wrong-answer path - fires ONLY on the street the user diverged). */
export interface CoachCallout {
  /** the bold lead ("Carry this forward." / "Where it went wrong."). */
  lead: string;
  /** the rest of the coaching sentence. */
  text: string;
  /** true = the wrong-answer callout (red border). */
  bad: boolean;
}

/** The deep-debrief payload the CoachDrawer injects into the shared glossary popover. Always the
 *  correct-line explanation (the teaching payload), regardless of what the user chose. */
export interface DebriefContent {
  title: string;
  body: string;
  /** the boxed example line: a bold label ("Sizing:", "Read:") + its text. */
  example: { label: string; text: string };
}

/** The fully-resolved reveal for one committed decision - everything <GradeReveal> needs. */
export interface ResolvedGrade {
  verdict: Verdict;
  axisA: AxisGrade;
  axisB: AxisGrade;
  coach: CoachCallout;
  debrief: DebriefContent;
}
